import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus } from 'lucide-react';
import { formatDateRange, formatDayShort } from '../../utils/ipoCalendar';
import { BOARD_STYLES } from './calendarStyles';

const COLUMNS = [
  { phase: 'upcoming', title: 'Upcoming', tone: 'border-t-sky-400', empty: 'No upcoming issues' },
  { phase: 'open', title: 'Bidding Open', tone: 'border-t-emerald-500', empty: 'Nothing open for bidding' },
  { phase: 'closed', title: 'Closed · Allotment Pending', tone: 'border-t-orange-400', empty: 'No issues awaiting allotment' },
  { phase: 'allotted', title: 'Allotment Finalised', tone: 'border-t-slate-400', empty: 'No allotments yet' },
];

const priceBand = (issue) => {
  if (!issue.priceHigh) return 'Price TBA';
  return issue.priceLow && issue.priceLow !== issue.priceHigh ? `₹${issue.priceLow} – ₹${issue.priceHigh}` : `₹${issue.priceHigh}`;
};

const milestone = (issue, phase, today) => {
  if (phase === 'upcoming') return `Opens ${formatDayShort(issue.openOn)}`;
  if (phase === 'open') return issue.closeOn === today ? 'Closes today, 5:00 PM IST' : `Closes ${formatDayShort(issue.closeOn)}, 5:00 PM`;
  if (phase === 'closed') return `Allotment ${formatDayShort(issue.allotmentOn)}${issue.allotmentEstimated ? ' (expected)' : ''}`;
  return `Allotted ${formatDayShort(issue.allotmentOn)}`;
};

// Issues grouped by where they stand today
export const PipelineView = ({ issues, today, onSyncIssue, onAction }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-4">
    {COLUMNS.map((column) => {
      const items = issues.filter((item) => item.phase === column.phase);
      return (
        <section key={column.phase} className={`bg-slate-50 rounded-2xl border border-slate-200/90 border-t-4 ${column.tone}`}>
          <div className="flex items-center justify-between px-4 py-3">
            <h3 className="text-sm font-extrabold text-slate-900">{column.title}</h3>
            <span className="px-2 py-0.5 rounded-full bg-white border border-slate-200 text-[11px] font-bold text-slate-600">
              {items.length}
            </span>
          </div>
          <ul className="px-3 pb-3 space-y-2">
            {items.length === 0 && <li className="px-1 py-4 text-center text-xs text-slate-400">{column.empty}</li>}
            {items.map(({ issue, phase }) => (
              <li key={issue.id} className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  {issue.gmpSlug ? (
                    <Link to={`/ipo-gmp/${issue.gmpSlug}`} className="text-sm font-bold text-slate-900 hover:text-[#008080] leading-snug">
                      {issue.company}
                    </Link>
                  ) : (
                    <span className="text-sm font-bold text-slate-900 leading-snug">{issue.company}</span>
                  )}
                  <span className={`px-1.5 py-px rounded border text-[10px] font-bold uppercase flex-shrink-0 ${BOARD_STYLES[issue.board].badge}`}>
                    {BOARD_STYLES[issue.board].label}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  {priceBand(issue)} • {formatDateRange(issue.openOn, issue.closeOn)}
                </p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold text-slate-700">{milestone(issue, phase, today)}</span>
                  {phase === 'upcoming' && (
                    <button
                      type="button"
                      onClick={() => onSyncIssue(issue)}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-[#008080] hover:underline"
                    >
                      <CalendarPlus className="w-3.5 h-3.5" />
                      Sync
                    </button>
                  )}
                  {phase === 'open' && (
                    <button
                      type="button"
                      onClick={() => onAction({ kind: 'apply', issue })}
                      className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold"
                    >
                      Apply
                    </button>
                  )}
                  {phase === 'allotted' && (
                    <button
                      type="button"
                      onClick={() => onAction({ kind: 'allotment', issue })}
                      className="text-[11px] font-bold text-[#008080] hover:underline"
                    >
                      Check Allotment
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      );
    })}
  </div>
);

export default PipelineView;
